import Cart from "cart-store";
import { formatMoney } from "utils";

class CustomVariantPicker extends HTMLElement {
  #frameProducts = [];
  #currentSize = "";
  #sizeOptionName = "";
  #materialOptionName = "";
  #excludedMaterials = [];
  #excludedSizes = [];
  #variantSelectsEl = null;
  #variantData = [];
  #boundFormSubmit = null;
  #boundVariantChange = null;
  #formEl = null;
  #activeDrawer = null;
  #editMode = false;
  #editLineItemKeys = [];

  connectedCallback() {
    requestAnimationFrame(() => this.#init());
  }

  #init() {
    this.#sizeOptionName = (this.dataset.sizeOptionName || "Formaat").toLowerCase();
    this.#materialOptionName = (this.dataset.materialOptionName || "Uitvoering").toLowerCase();
    this.#excludedMaterials = (this.dataset.excludedMaterials || "Dibond")
      .split(",")
      .map((v) => v.trim().toLowerCase())
      .filter(Boolean);
    this.#excludedSizes = (this.dataset.excludedSizes || "")
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);

    this.#variantSelectsEl = this.querySelector("variant-selects");

    if (this.#variantSelectsEl) {
      const jsonEl = this.#variantSelectsEl.querySelector('[type="application/json"]');
      if (jsonEl) {
        try {
          this.#variantData = JSON.parse(jsonEl.textContent);
        } catch (_) {}
      }
    }

    const frameDataEl = this.querySelector("[data-frame-products]");
    if (frameDataEl) {
      try {
        this.#frameProducts = JSON.parse(frameDataEl.textContent);
      } catch (_) {}
    }

    this.#initCurrentValues();
    this.#hoistDrawers();
    this.#bindRows();
    this.#bindCloseButtons();
    this.#bindOverlayClicks();
    this.#bindOptionButtons();
    this.#updateAllRowPrices();
    this.#updateFrameOptions();
    this.#checkInitialFrameAvailability();

    if (this.#variantSelectsEl) {
      this.#boundVariantChange = this.#onVariantChange.bind(this);
      this.#variantSelectsEl.addEventListener(
        "on:variant:change",
        this.#boundVariantChange
      );

      this.#variantSelectsEl.querySelectorAll("select").forEach((sel) => {
        sel.addEventListener("change", () => {
          this.#syncSizeFromSelects();
        });
      });
    }

    const productSingle = this.closest("product-single");
    if (productSingle) {
      this.#interceptFormSubmit(productSingle);
    }

    this.#initEditMode();
  }

  disconnectedCallback() {
    if (this.#formEl && this.#boundFormSubmit) {
      this.#formEl.removeEventListener("submit", this.#boundFormSubmit, true);
    }
    document.body.querySelectorAll(".cvp__drawer").forEach((d) => {
      if (this.querySelector(`.cvp__picker[data-cvp-id="${d.dataset.cvpPicker}"]`)) {
        d.remove();
      }
    });
  }

  #initCurrentValues() {
    if (!this.#variantSelectsEl) return;
    const selects = this.#variantSelectsEl.querySelectorAll("select");
    selects.forEach((sel) => {
      const optionName = this.#getOptionNameForSelect(sel);
      if (optionName === this.#sizeOptionName) {
        this.#currentSize = sel.value;
      }
    });

    if (!this.#currentSize) {
      const sizePicker = this.querySelector(
        `.cvp__picker[data-option-name="${this.dataset.sizeOptionName || "Formaat"}"]`
      );
      if (sizePicker) {
        const rowVal = sizePicker.querySelector(".cvp__row-value");
        if (rowVal) this.#currentSize = rowVal.textContent.trim();
      }
    }
  }

  #getOptionNameForSelect(selectEl) {
    const name = selectEl.getAttribute("name") || "";
    const match = name.match(/options\[(.+?)\]/);
    if (match) return match[1].toLowerCase();
    const picker = selectEl.closest(".cvp__picker");
    if (picker) return (picker.dataset.optionName || "").toLowerCase();
    return "";
  }

  // ── Hoist drawers to body so position:fixed works regardless of parent transforms ──

  #hoistDrawers() {
    this.querySelectorAll(".cvp__drawer").forEach((drawer) => {
      const picker = drawer.closest(".cvp__picker");
      const pickerId = picker?.dataset.optionName || Math.random().toString(36).slice(2);
      drawer.dataset.cvpPicker = pickerId;
      picker.dataset.cvpId = pickerId;
      document.body.appendChild(drawer);
    });
  }

  #getDrawerForPicker(picker) {
    const id = picker.dataset.cvpId;
    return id ? document.body.querySelector(`.cvp__drawer[data-cvp-picker="${id}"]`) : null;
  }

  #syncSizeFromSelects() {
    const selects = this.#variantSelectsEl?.querySelectorAll("select") || [];
    let newSize = "";
    selects.forEach((sel) => {
      const optionName = this.#getOptionNameForSelect(sel);
      if (optionName === this.#sizeOptionName) newSize = sel.value;
    });
    if (newSize && newSize !== this.#currentSize) {
      this.#currentSize = newSize;
      this.#refreshFrameState();
    }
  }

  #refreshFrameState() {
    const framePicker = this.querySelector(".cvp__picker--frame");
    if (!framePicker || framePicker.classList.contains("cvp__picker--hidden")) return;

    if (this.#isSizeExcludedFromFrames(this.#currentSize)) {
      this.#setFrameRowDisabled(framePicker, true);
    } else {
      this.#setFrameRowDisabled(framePicker, false);
      this.#updateFrameOptions();
    }
  }

  // ── Drawer open/close ──

  #getPickerForDrawer(drawer) {
    const id = drawer.dataset.cvpPicker;
    return id ? this.querySelector(`.cvp__picker[data-cvp-id="${id}"]`) : null;
  }

  #openDrawer(drawer) {
    if (this.#activeDrawer) this.#closeDrawer(this.#activeDrawer);
    this.#activeDrawer = drawer;
    drawer.setAttribute("aria-hidden", "false");
    window.theme?.scrollLock?.lock();

    const picker = this.#getPickerForDrawer(drawer);
    const row = picker?.querySelector(".cvp__row");
    if (row) row.setAttribute("aria-expanded", "true");
  }

  #closeDrawer(drawer, callback) {
    if (!drawer || drawer.getAttribute("aria-hidden") === "true") return;

    drawer.classList.add("is-closing");

    const panel = drawer.querySelector(".cvp__drawer-panel");
    const onEnd = () => {
      panel.removeEventListener("transitionend", onEnd);
      drawer.setAttribute("aria-hidden", "true");
      drawer.classList.remove("is-closing");
      if (this.#activeDrawer === drawer) this.#activeDrawer = null;
      window.theme?.scrollLock?.unlock();

      const picker = this.#getPickerForDrawer(drawer);
      const row = picker?.querySelector(".cvp__row");
      if (row) row.setAttribute("aria-expanded", "false");

      if (callback) callback();
    };

    panel.addEventListener("transitionend", onEnd);
    setTimeout(() => {
      if (drawer.classList.contains("is-closing")) onEnd();
    }, 300);
  }

  #closeAllDrawers() {
    document.body.querySelectorAll('.cvp__drawer[aria-hidden="false"]').forEach((d) =>
      this.#closeDrawer(d)
    );
  }

  #bindRows() {
    this.querySelectorAll("[data-drawer-trigger]").forEach((row) => {
      row.addEventListener("click", (e) => {
        e.stopPropagation();
        if (row.classList.contains("cvp__row--disabled")) return;

        const picker = row.closest(".cvp__picker");
        const drawer = this.#getDrawerForPicker(picker);
        if (!drawer) return;

        const isOpen = drawer.getAttribute("aria-hidden") === "false";
        if (isOpen) {
          this.#closeDrawer(drawer);
        } else {
          this.#openDrawer(drawer);
        }
      });
    });
  }

  #bindCloseButtons() {
    const drawers = document.body.querySelectorAll(`.cvp__drawer`);
    drawers.forEach((drawer) => {
      if (!this.#getPickerForDrawer(drawer)) return;
      drawer.querySelectorAll(".cvp__drawer-close").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          this.#closeDrawer(drawer);
        });
      });
    });
  }

  #bindOverlayClicks() {
    const drawers = document.body.querySelectorAll(`.cvp__drawer`);
    drawers.forEach((drawer) => {
      if (!this.#getPickerForDrawer(drawer)) return;
      const overlay = drawer.querySelector(".cvp__drawer-overlay");
      if (overlay) {
        overlay.addEventListener("click", (e) => {
          e.stopPropagation();
          this.#closeDrawer(drawer);
        });
      }
    });
  }

  // ── Option selection ──

  #bindOptionButtons() {
    const drawers = document.body.querySelectorAll(`.cvp__drawer`);
    drawers.forEach((drawer) => {
      const picker = this.#getPickerForDrawer(drawer);
      if (!picker) return;

      drawer.querySelectorAll(".cvp__option").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          if (btn.disabled) return;

          const isFrame = picker.dataset.optionName === "frame";
          const value = isFrame ? btn.dataset.frameHandle : btn.dataset.value;

          if (isFrame) {
            this.#selectFrameOption(picker, btn, value, drawer);
          } else {
            this.#selectVariantOption(picker, btn, value, drawer);
          }

          this.#closeDrawer(drawer);
        });
      });
    });
  }

  #selectVariantOption(picker, btn, value, drawer) {
    const optionIndex = parseInt(picker.dataset.optionIndex, 10);
    const select = this.#variantSelectsEl?.querySelector(
      `select[data-option-index="${optionIndex}"]`
    );
    if (select) {
      select.value = value;
      select.dispatchEvent(new Event("change", { bubbles: true }));
    }

    const optionName = (picker.dataset.optionName || "").toLowerCase();
    if (optionName === this.#sizeOptionName) {
      this.#currentSize = value;
      this.#refreshFrameState();
    }

    if (drawer) {
      drawer.querySelectorAll(".cvp__option").forEach((opt) => {
        opt.classList.toggle("cvp__option--selected", opt === btn);
      });
    }

    const rowValue = picker.querySelector(".cvp__row-value");
    if (rowValue) rowValue.textContent = value;

    const swatchBg = btn.dataset.swatch || "";
    const rowSwatch = picker.querySelector(".cvp__row-swatch");
    if (rowSwatch && swatchBg) {
      rowSwatch.style.setProperty("--swatch-bg", swatchBg);
      rowSwatch.classList.remove("cvp__row-swatch--none");
    } else if (rowSwatch && !swatchBg) {
      rowSwatch.style.removeProperty("--swatch-bg");
      rowSwatch.classList.add("cvp__row-swatch--none");
      rowSwatch.innerHTML = "";
    }
  }

  #emitFrameChange(handle) {
    this.dispatchEvent(new CustomEvent("on:frame:change", {
      bubbles: true,
      detail: { handle: handle || null, sectionId: this.dataset.section || "" },
    }));
  }

  #selectFrameOption(picker, btn, handle, drawer) {
    const hiddenSelect = picker.querySelector("select.frame-selector__select");
    if (hiddenSelect) {
      hiddenSelect.value = handle;
      this.#emitFrameChange(handle);
    }

    if (drawer) {
      drawer.querySelectorAll(".cvp__option").forEach((opt) => {
        opt.classList.toggle("cvp__option--selected", opt === btn);
      });
    }

    const rowValue = picker.querySelector("[data-frame-value]");
    if (rowValue) {
      rowValue.textContent = btn.querySelector(".cvp__option-name")?.textContent || "Geen lijst";
    }

    const rowSwatch = picker.querySelector("[data-frame-swatch]");
    if (rowSwatch) {
      if (handle) {
        const img = btn.querySelector(".cvp__option-swatch img");
        if (img) {
          rowSwatch.innerHTML = `<img src="${img.src}" width="44" height="44" alt="" style="width:100%;height:100%;object-fit:cover;border-radius:5px">`;
          rowSwatch.classList.remove("cvp__row-swatch--none");
          rowSwatch.hidden = false;
        } else {
          const swatchBg = btn.querySelector(".cvp__option-swatch")?.style?.getPropertyValue("--swatch-bg");
          if (swatchBg) {
            rowSwatch.innerHTML = "";
            rowSwatch.style.setProperty("--swatch-bg", swatchBg);
            rowSwatch.classList.remove("cvp__row-swatch--none");
            rowSwatch.hidden = false;
          } else {
            rowSwatch.innerHTML = "";
            rowSwatch.classList.add("cvp__row-swatch--none");
            rowSwatch.hidden = false;
          }
        }
      } else {
        rowSwatch.innerHTML = "";
        rowSwatch.classList.add("cvp__row-swatch--none");
        rowSwatch.hidden = true;
      }
    }

    const rowPrice = picker.querySelector("[data-frame-row-price]");
    if (rowPrice) {
      const frame = this.#getSelectedFrameData();
      if (frame) {
        rowPrice.innerHTML = this.#formatPriceWithCompare(frame.price, frame.compareAtPrice, window.theme.money_format, "+");
      } else {
        rowPrice.textContent = "";
      }
    }

    this.#updateCustomPriceBlock();
  }

  // ── Variant change handler ──

  #onVariantChange(e) {
    const variant = e.detail?.variant;

    let newSize = "";
    let newMaterial = "";

    const selects = this.#variantSelectsEl?.querySelectorAll("select") || [];
    selects.forEach((sel) => {
      const optionName = this.#getOptionNameForSelect(sel);
      if (optionName === this.#sizeOptionName) newSize = sel.value;
      if (optionName === this.#materialOptionName) newMaterial = sel.value;
    });

    this.#currentSize = newSize;

    if (variant) {
      this.#updateAllRowPrices(variant);
      this.#updateRowSelections();
    }

    const isExcluded = this.#excludedMaterials.some((ex) =>
      newMaterial.toLowerCase().includes(ex)
    );

    const framePicker = this.querySelector('.cvp__picker--frame');
    if (framePicker) {
      if (isExcluded) {
        framePicker.classList.add("cvp__picker--hidden");
        this.#resetFrameSelection(framePicker);
      } else {
        framePicker.classList.remove("cvp__picker--hidden");
        if (this.#isSizeExcludedFromFrames(newSize)) {
          this.#setFrameRowDisabled(framePicker, true);
        } else {
          this.#setFrameRowDisabled(framePicker, false);
          this.#updateFrameOptions();
        }
      }
    }

    if (variant) {
      this.#updateCustomPriceBlock(variant);
    }
  }

  #updateRowSelections() {
    const selects = this.#variantSelectsEl?.querySelectorAll("select") || [];
    selects.forEach((sel) => {
      const idx = sel.dataset.optionIndex;
      const picker = this.querySelector(`.cvp__picker[data-option-index="${idx}"]`);
      if (!picker) return;

      const drawer = this.#getDrawerForPicker(picker);
      const rowValue = picker.querySelector(".cvp__row-value");
      if (rowValue) rowValue.textContent = sel.value;

      if (drawer) {
        drawer.querySelectorAll(".cvp__option").forEach((opt) => {
          opt.classList.toggle(
            "cvp__option--selected",
            opt.dataset.value === sel.value
          );
        });

        const selectedBtn = drawer.querySelector(
          `.cvp__option[data-value="${CSS.escape(sel.value)}"]`
        );
        const rowSwatch = picker.querySelector(".cvp__row-swatch");
        if (selectedBtn && rowSwatch) {
          const swatchBg = selectedBtn.dataset.swatch || "";
          if (swatchBg) {
            rowSwatch.style.setProperty("--swatch-bg", swatchBg);
            rowSwatch.classList.remove("cvp__row-swatch--none");
            rowSwatch.innerHTML = "";
          } else {
            rowSwatch.style.removeProperty("--swatch-bg");
            rowSwatch.classList.add("cvp__row-swatch--none");
            rowSwatch.innerHTML = "";
          }
        }
      }
    });
  }

  #updateAllRowPrices(variant) {
    if (!variant) {
      const vs = this.#variantSelectsEl;
      if (vs?.currentVariant) {
        variant = vs.currentVariant;
      } else {
        const selects = vs?.querySelectorAll("select") || [];
        const selectedOptions = Array.from(selects, (s) => s.value);
        variant = this.#variantData.find(
          (v) => !v.options.map((o, i) => selectedOptions[i] === o).includes(false)
        );
      }
    }
    if (!variant) return;

    const fmt = window.theme.money_format;

    this.querySelectorAll('.cvp__picker:not(.cvp__picker--frame)').forEach((picker) => {
      const rowPrice = picker.querySelector("[data-variant-price]");
      if (rowPrice && !rowPrice.hasAttribute("data-hide-row-price")) {
        rowPrice.innerHTML = this.#formatPriceWithCompare(variant.price, variant.compare_at_price, fmt);
      }

      const optionIndex = parseInt(picker.dataset.optionIndex, 10);
      const drawer = this.#getDrawerForPicker(picker);
      if (!drawer) return;

      drawer.querySelectorAll(".cvp__option").forEach((opt) => {
        const optValue = opt.dataset.value;
        const selects = this.#variantSelectsEl?.querySelectorAll("select") || [];
        const hypotheticalOptions = Array.from(selects, (s) => s.value);
        hypotheticalOptions[optionIndex] = optValue;

        const matchedVariant = this.#variantData.find(
          (v) => !v.options.map((o, i) => hypotheticalOptions[i] === o).includes(false)
        );

        const priceEl = opt.querySelector(".cvp__option-price");
        if (priceEl && matchedVariant) {
          priceEl.innerHTML = this.#formatPriceWithCompare(matchedVariant.price, matchedVariant.compare_at_price, fmt);
        } else if (priceEl) {
          priceEl.textContent = "";
        }
      });
    });
  }

  #formatPriceWithCompare(price, compareAtPrice, fmt, prefix = "") {
    const isOnSale = compareAtPrice && compareAtPrice > price;
    if (isOnSale) {
      return `<s class="cvp__price-compare">${formatMoney(compareAtPrice, fmt)}</s> ${prefix}${formatMoney(price, fmt)}`;
    }
    return `${prefix}${formatMoney(price, fmt)}`;
  }

  // ── Frame logic ──

  #isSizeExcludedFromFrames(size) {
    return this.#excludedSizes.some(
      (ex) => ex.toLowerCase() === size.toLowerCase()
    );
  }

  #checkInitialFrameAvailability() {
    const framePicker = this.querySelector('.cvp__picker--frame');
    if (!framePicker || framePicker.classList.contains("cvp__picker--hidden")) return;
    if (!this.#currentSize) return;

    if (this.#isSizeExcludedFromFrames(this.#currentSize)) {
      this.#setFrameRowDisabled(framePicker, true);
      return;
    }

    if (this.#frameProducts.length === 0) return;

    const anyFrameAvailable = this.#frameProducts.some((fp) =>
      fp.variants.some((v) => v.option1 === this.#currentSize && v.available)
    );
    this.#setFrameRowDisabled(framePicker, !anyFrameAvailable);
  }

  #updateFrameOptions() {
    const framePicker = this.querySelector('.cvp__picker--frame');
    if (!framePicker) return;

    if (this.#currentSize && this.#isSizeExcludedFromFrames(this.#currentSize)) {
      this.#setFrameRowDisabled(framePicker, true);
      return;
    }

    const hiddenSelect = framePicker.querySelector("select.frame-selector__select");
    const frameDrawer = this.#getDrawerForPicker(framePicker);
    if (!frameDrawer) return;

    frameDrawer.querySelectorAll(".cvp__option[data-frame-handle]").forEach((opt) => {
      const handle = opt.dataset.frameHandle;
      if (!handle) return;

      const frameProduct = this.#frameProducts.find((f) => f.handle === handle);
      if (!frameProduct) return;

      const matchingVariant = frameProduct.variants.find(
        (v) => v.option1 === this.#currentSize && v.available
      );

      const priceEl = opt.querySelector(".cvp__option-price");

      if (matchingVariant) {
        opt.disabled = false;
        opt.classList.remove("cvp__option--disabled");
        if (priceEl) {
          priceEl.innerHTML = this.#formatPriceWithCompare(matchingVariant.price, matchingVariant.compare_at_price, window.theme.money_format, "+");
        }
      } else {
        opt.disabled = true;
        opt.classList.add("cvp__option--disabled");
        if (priceEl) {
          priceEl.innerHTML = '<span class="cvp__option-unavailable">Niet beschikbaar</span>';
        }
        if (opt.classList.contains("cvp__option--selected")) {
          this.#resetFrameSelection(framePicker);
        }
      }

      if (hiddenSelect) {
        const selectOpt = hiddenSelect.querySelector(`option[value="${handle}"]`);
        if (selectOpt) {
          selectOpt.disabled = !matchingVariant;
          if (matchingVariant) {
            selectOpt.dataset.price = matchingVariant.price;
          }
        }
      }
    });

    if (this.#currentSize && this.#frameProducts.length > 0) {
      const anyFrameAvailable = this.#frameProducts.some((fp) =>
        fp.variants.some((v) => v.option1 === this.#currentSize && v.available)
      );
      this.#setFrameRowDisabled(framePicker, !anyFrameAvailable);
    }

    const frame = this.#getSelectedFrameData();
    const rowPrice = framePicker.querySelector("[data-frame-row-price]");
    if (rowPrice) {
      if (frame) {
        rowPrice.innerHTML = this.#formatPriceWithCompare(frame.price, frame.compareAtPrice, window.theme.money_format, "+");
      } else {
        rowPrice.textContent = "";
      }
    }

    this.#updateCustomPriceBlock();
  }

  #setFrameRowDisabled(framePicker, disabled) {
    const row = framePicker.querySelector(".cvp__row");
    const labelEl = framePicker.querySelector(".cvp__row-label");
    const swatchEl = framePicker.querySelector("[data-frame-swatch]");
    const valueEl = framePicker.querySelector("[data-frame-value]");
    const priceEl = framePicker.querySelector("[data-frame-row-price]");
    const chevronEl = framePicker.querySelector(".cvp__row-chevron");
    const noteEl = framePicker.querySelector("[data-frame-note]");

    if (disabled) {
      row?.classList.add("cvp__row--disabled");
      if (labelEl) labelEl.hidden = true;
      if (swatchEl) swatchEl.hidden = true;
      if (valueEl) valueEl.hidden = true;
      if (priceEl) priceEl.hidden = true;
      if (chevronEl) chevronEl.hidden = true;
      if (noteEl) noteEl.hidden = false;
      this.#resetFrameSelection(framePicker);
    } else {
      row?.classList.remove("cvp__row--disabled");
      if (labelEl) labelEl.hidden = false;
      const hiddenSelect = framePicker.querySelector("select.frame-selector__select");
      const hasFrame = hiddenSelect?.value && hiddenSelect.value !== "";
      if (swatchEl) swatchEl.hidden = !hasFrame;
      if (valueEl) valueEl.hidden = false;
      if (priceEl) priceEl.hidden = false;
      if (chevronEl) chevronEl.hidden = false;
      if (noteEl) noteEl.hidden = true;
    }
  }

  #resetFrameSelection(framePicker) {
    const hiddenSelect = framePicker.querySelector("select.frame-selector__select");
    if (hiddenSelect) hiddenSelect.value = "";

    const frameDrawer = this.#getDrawerForPicker(framePicker);
    if (frameDrawer) {
      frameDrawer.querySelectorAll(".cvp__option").forEach((opt) => {
        opt.classList.toggle("cvp__option--selected", opt.dataset.frameHandle === "");
      });
    }

    const rowValue = framePicker.querySelector("[data-frame-value]");
    if (rowValue) rowValue.textContent = "Geen lijst";

    const rowSwatch = framePicker.querySelector("[data-frame-swatch]");
    if (rowSwatch) {
      rowSwatch.innerHTML = "";
      rowSwatch.classList.add("cvp__row-swatch--none");
      rowSwatch.hidden = true;
    }

    const rowPrice = framePicker.querySelector("[data-frame-row-price]");
    if (rowPrice) rowPrice.textContent = "";

    this.#emitFrameChange(null);
    this.#updateCustomPriceBlock();
  }

  #getSelectedFrameData() {
    const framePicker = this.querySelector('.cvp__picker--frame');
    if (!framePicker) return null;
    const hiddenSelect = framePicker.querySelector("select.frame-selector__select");
    if (!hiddenSelect || !hiddenSelect.value) return null;

    const handle = hiddenSelect.value;
    const frameProduct = this.#frameProducts.find((f) => f.handle === handle);
    if (!frameProduct) return null;

    const matchingVariant = frameProduct.variants.find(
      (v) => v.option1 === this.#currentSize && v.available
    );
    if (!matchingVariant) return null;

    return {
      variantId: matchingVariant.id,
      title: frameProduct.title,
      price: matchingVariant.price,
      compareAtPrice: matchingVariant.compare_at_price || 0,
      image: frameProduct.image || "",
    };
  }

  // ── Custom price block ──

  #updateCustomPriceBlock(variant) {
    const sectionId = this.dataset.section;
    const priceBlock = document.getElementById(`custom-price-${sectionId}`);
    if (!priceBlock) return;

    if (!variant) {
      const vs = this.#variantSelectsEl;
      if (vs?.currentVariant) {
        variant = vs.currentVariant;
      } else {
        const selects = vs?.querySelectorAll("select") || [];
        const selectedOptions = Array.from(selects, (s) => s.value);
        variant = this.#variantData.find(
          (v) => !v.options.map((o, i) => selectedOptions[i] === o).includes(false)
        );
      }
    }
    if (!variant) return;

    const frame = this.#getSelectedFrameData();
    const printPrice = variant.price;
    const printCompare = variant.compare_at_price || 0;
    const framePrice = frame ? frame.price : 0;
    const frameCompare = frame ? (frame.compareAtPrice || 0) : 0;

    const displayPrice = printPrice + framePrice;
    const printOriginal = printCompare > printPrice ? printCompare : printPrice;
    const frameOriginal = frameCompare > framePrice ? frameCompare : framePrice;
    const compareTotal = printOriginal + frameOriginal;

    const printPriceEl = priceBlock.querySelector("[data-print-price]");
    const printWrapEl = priceBlock.querySelector("[data-print-wrap]");
    const comparePriceEl = priceBlock.querySelector("[data-compare-price]");
    const framePriceEl = priceBlock.querySelector("[data-frame-price]");
    const totalEl = priceBlock.querySelector("[data-total-price]");
    const fmt = window.theme.money_format;
    const priceFmt = window.theme.money_product_price_format || fmt;

    const isOnSale = compareTotal > displayPrice;

    if (printPriceEl) {
      printPriceEl.textContent = formatMoney(displayPrice, priceFmt);
    }
    if (printWrapEl) {
      printWrapEl.classList.toggle("price__number--sale", isOnSale);
    }
    if (comparePriceEl) {
      const moneyEl = comparePriceEl.querySelector(".money");
      if (moneyEl) moneyEl.textContent = formatMoney(compareTotal, fmt);
      comparePriceEl.hidden = !isOnSale;
    }

    if (framePriceEl) framePriceEl.hidden = true;
    if (totalEl) totalEl.hidden = true;
  }

  // ── Edit mode ──

  async #initEditMode() {
    const params = new URLSearchParams(window.location.search);
    const editKey = params.get("edit");
    if (!editKey) return;

    let cart = Cart.getState().cart;
    if (!cart || !cart.items || cart.items.length === 0) {
      try {
        cart = await Cart.refresh();
      } catch (_) {
        return;
      }
    }
    if (!cart || !cart.items) return;

    const editItem = cart.items.find((i) => i.key === editKey);
    if (!editItem) return;

    this.#editMode = true;
    this.#editLineItemKeys = [editKey];

    const bundleId = editItem.properties?._bundle_id;
    if (bundleId) {
      cart.items.forEach((i) => {
        if (i.properties?._bundle_id === bundleId && !this.#editLineItemKeys.includes(i.key)) {
          this.#editLineItemKeys.push(i.key);
        }
      });
    }

    const variantId = editItem.variant_id;
    const variant = this.#variantData.find((v) => v.id === variantId);
    if (variant && this.#variantSelectsEl) {
      const selects = this.#variantSelectsEl.querySelectorAll("select");
      selects.forEach((sel, idx) => {
        if (variant.options[idx]) {
          sel.value = variant.options[idx];
          sel.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    }

    if (bundleId) {
      const frameItem = cart.items.find(
        (i) => i.properties?._bundle_id === bundleId && i.properties?._is_frame === "true"
      );
      if (frameItem) {
        const frameHandle = this.#findFrameHandleByVariantId(frameItem.variant_id);
        if (frameHandle) {
          const framePicker = this.querySelector(".cvp__picker--frame");
          const drawer = framePicker ? this.#getDrawerForPicker(framePicker) : null;
          if (drawer) {
            const frameBtn = drawer.querySelector(`.cvp__option[data-frame-handle="${CSS.escape(frameHandle)}"]`);
            if (frameBtn) {
              this.#selectFrameOption(framePicker, frameBtn, frameHandle, drawer);
            }
          }
        }
      }
    }

    this.#updateEditModeButton();
  }

  #findFrameHandleByVariantId(variantId) {
    for (const fp of this.#frameProducts) {
      if (fp.variants.some((v) => v.id === variantId)) {
        return fp.handle;
      }
    }
    return null;
  }

  #updateEditModeButton() {
    if (!this.#editMode) return;
    const productSingle = this.closest("product-single");
    if (!productSingle) return;

    const addBtn = productSingle.querySelector(".js-product-add:not(.js-product-checkout)");
    if (!addBtn) return;

    const textEl = addBtn.querySelector("staged-action-text");
    const saveText = this.dataset.saveChangesText || "Save changes";
    if (textEl) {
      textEl.textContent = saveText;
    }

    const priceTag = addBtn.querySelector(".cvp__add-price, .product-form__add-price");
    if (priceTag) priceTag.hidden = false;
  }

  // ── Form submit interception ──

  #interceptFormSubmit(productSingle) {
    const productFormComponent = productSingle.querySelector("product-form");
    if (!productFormComponent) return;

    const findForm = () => {
      const form = productFormComponent.querySelector("form");
      if (!form) return;
      this.#formEl = form;
      this.#boundFormSubmit = this.#handleFormSubmit.bind(this, productFormComponent);
      form.addEventListener("submit", this.#boundFormSubmit, true);
    };

    if (productFormComponent.querySelector("form")) {
      findForm();
    } else {
      setTimeout(findForm, 50);
    }
  }

  async #handleFormSubmit(productFormComponent, e) {
    const frame = this.#getSelectedFrameData();
    if (!frame && !this.#editMode) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    const form = e.target;
    const data = new FormData(form);
    const printItem = Object.fromEntries(data.entries());
    const submitButton = e.submitter || form.elements.add;
    const isCheckoutButton =
      submitButton && submitButton.classList.contains("js-product-checkout");
    const checkoutUrl = submitButton?.dataset?.checkoutUrl || "/checkout";
    const stagedAction = productFormComponent.querySelector("staged-action");
    const notifyOnAdd = productFormComponent.hasAttribute("notify-on-add");
    const redirectOnAdd = productFormComponent.getAttribute("redirect-on-add");

    const qty = parseInt(printItem.quantity, 10) || 1;
    let items;

    if (frame) {
      const bundleId = Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
      items = [
        {
          id: printItem.id,
          quantity: qty,
          "properties[_bundle_id]": bundleId,
          "properties[_frame_title]": frame.title,
          "properties[_edit_url]": window.location.pathname,
        },
        {
          id: frame.variantId.toString(),
          quantity: qty,
          "properties[_bundle_id]": bundleId,
          "properties[_is_frame]": "true",
          "properties[_frame_title]": frame.title,
        },
      ];
    } else {
      items = [
        {
          id: printItem.id,
          quantity: qty,
          "properties[_edit_url]": window.location.pathname,
        },
      ];
    }

    try {
      const errorEl = productFormComponent.querySelector(".errors");
      if (errorEl) errorEl.remove();
      if (stagedAction) await stagedAction.setState("DOING");

      if (this.#editMode && this.#editLineItemKeys.length > 0) {
        for (const key of this.#editLineItemKeys) {
          await Cart.change({ id: key, quantity: 0 });
        }
        this.#editMode = false;
        this.#editLineItemKeys = [];
      }

      await Cart.add(items);

      if (isCheckoutButton) {
        window.location.href = checkoutUrl;
        return;
      }
      if (redirectOnAdd) {
        window.location.href = redirectOnAdd;
        return;
      }
      if (stagedAction) await stagedAction.setState("DONE");

      const url = new URL(window.location);
      url.searchParams.delete("edit");
      window.history.replaceState({}, "", url);

      const quickShop = productFormComponent.closest("quick-shop");
      if (quickShop) await quickShop.close();

      if (notifyOnAdd) {
        await Cart.resetVariantsBeingAdded();
        if (!productFormComponent.closest(".modal--cart")) {
          const cartModal = document.querySelector(
            ".modal--cart, .popup--cart-notification"
          );
          if (cartModal && !cartModal.shown) {
            cartModal.open();
          } else if (cartModal && cartModal.interval) {
            cartModal.interval.reset();
            cartModal.interval.start();
          }
        }
      } else {
        await Cart.resetLatestAddedProduct();
        await Cart.resetVariantsBeingAdded();
      }
    } catch (error) {
      const existingError = productFormComponent.querySelector(".errors");
      if (existingError) existingError.remove();
      const addBtn = form.elements.add;
      if (addBtn) {
        const errorDiv = document.createElement("div");
        errorDiv.classList.add("errors", "qty-error", "u-small");
        errorDiv.innerHTML = error.message;
        addBtn.before(errorDiv);
      }
      if (stagedAction) await stagedAction.setState("IDLE");
    }
  }
}

customElements.define("custom-variant-picker", CustomVariantPicker);

export default CustomVariantPicker;
